/**
 * Bridge between FE scopeSheet (lettered sections) and Interior Scope API document.
 * Program fields align with ultimate-scope v1.0.0 (bedroom_count, balcony_count, …).
 * Room answers still use local schema until full template-driven UI ships; keys are
 * flattened as subsection__field for PATCH round-trips.
 */
import { getBedroomCountFromScope } from './scopeSheetSchema';
import {
  emptyScopeSheet,
  migrateScopeSheetKeys,
} from './scopeSheetDefaults';
import { emptyTipTapDoc, emptyTipTapNotes } from './interiorScopeApi';

const AREA_BY_SECTION = {
  D: 'main_entrance',
  E: 'foyer',
  F: 'living_drawing',
  G: 'dining',
  H: 'kitchen',
};

const SECTION_BY_AREA = Object.fromEntries(
  Object.entries(AREA_BY_SECTION).map(([k, v]) => [v, k])
);

const isPlainObject = (v) => v && typeof v === 'object' && !Array.isArray(v);

const flattenAnswers = (sectionData = {}) => {
  const answers = {};
  Object.entries(sectionData).forEach(([subsectionId, values]) => {
    if (!isPlainObject(values)) return;
    if (subsectionId === 'balconies') return;
    Object.entries(values).forEach(([fieldId, value]) => {
      answers[`${subsectionId}__${fieldId}`] = value;
    });
  });
  return answers;
};

const unflattenAnswers = (answers = {}) => {
  const section = {};
  Object.entries(answers || {}).forEach(([key, value]) => {
    const sep = key.indexOf('__');
    if (sep <= 0) {
      if (!section._root) section._root = {};
      section._root[key] = value;
      return;
    }
    const subsectionId = key.slice(0, sep);
    const fieldId = key.slice(sep + 2);
    if (!section[subsectionId]) section[subsectionId] = {};
    section[subsectionId][fieldId] = value;
  });
  return section;
};

const htmlToTipTap = (html) => {
  const text = String(html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  if (!text) return emptyTipTapDoc();
  return {
    type: 'doc',
    content: text.split(/\n+/).map((line) => ({
      type: 'paragraph',
      content: line ? [{ type: 'text', text: line }] : [],
    })),
  };
};

const tipTapToPlain = (doc) => {
  if (!doc || typeof doc !== 'object') return '';
  const walk = (node) => {
    if (!node) return '';
    if (node.type === 'text') return node.text || '';
    const kids = Array.isArray(node.content) ? node.content.map(walk).join('') : '';
    if (node.type === 'paragraph' || node.type === 'listItem') return `${kids}\n`;
    return kids;
  };
  return walk(doc).trim();
};

export const normalizeTipTapNotes = (notes) => {
  if (!notes) return emptyTipTapNotes();
  if (notes.format === 'tiptap' && notes.doc) {
    return { format: 'tiptap', doc: notes.doc };
  }
  if (typeof notes === 'string') {
    return { format: 'tiptap', doc: htmlToTipTap(notes) };
  }
  if (notes.doc) return { format: 'tiptap', doc: notes.doc };
  return emptyTipTapNotes();
};

const firstMulti = (value) => {
  if (Array.isArray(value)) return value[0] || '';
  return value || '';
};

const parseBedroomCount = (scopeSheet) => getBedroomCountFromScope(scopeSheet);

const parseBalconyCount = (scopeSheet) => {
  const list = scopeSheet?.balconies?.balconies;
  if (!Array.isArray(list) || list.length === 0) return 0;
  return list.length;
};

/** Build program payload from FE scope sheet. */
export const scopeSheetToProgram = (scopeSheet = {}) => {
  const basic = scopeSheet?.A?.basicInfo || {};
  const services = scopeSheet?.B?.servicesRequired?.selected || [];
  const deliverables = scopeSheet?.C?.deliverables?.selected || [];
  const help = scopeSheet?.['help-room'];
  const store = scopeSheet?.['store-room'];
  const hasHelp = help && Object.values(help).some((sub) =>
    Object.values(sub || {}).some((v) =>
      Array.isArray(v) ? v.length > 0 : String(v || '').trim()
    )
  );
  const hasStore = store && Object.values(store).some((sub) =>
    Object.values(sub || {}).some((v) =>
      Array.isArray(v) ? v.length > 0 : String(v || '').trim()
    )
  );

  return {
    bedroom_count: parseBedroomCount(scopeSheet),
    balcony_count: parseBalconyCount(scopeSheet),
    help_room_count: hasHelp ? 1 : Number(basic.helpRoomCount || 0) || 0,
    store_room_count: hasStore ? 1 : Number(basic.storeRoomCount || 0) || 0,
    project_address: String(basic.projectAddress || '').trim(),
    client_names: String(basic.clientNames || '').trim(),
    property_type: firstMulti(basic.propertyType),
    services_required: Array.isArray(services) ? services : [],
    deliverables: Array.isArray(deliverables) ? deliverables : [],
    unit_type: firstMulti(basic.unitType),
    total_carpet_area: String(basic.totalCarpetArea || '').trim(),
  };
};

const areaNotesFromSection = (sectionData) => {
  const notesHtml = sectionData?.notes?.notes;
  if (!notesHtml) return emptyTipTapNotes();
  if (typeof notesHtml === 'object' && notesHtml.format === 'tiptap') {
    return normalizeTipTapNotes(notesHtml);
  }
  return { format: 'tiptap', doc: htmlToTipTap(notesHtml) };
};

/** Convert FE scopeSheet → API PATCH/document fragments. */
export const scopeSheetToApiPayload = (scopeSheet, existingScope = null) => {
  const sheet = migrateScopeSheetKeys(scopeSheet) || emptyScopeSheet();
  const program = scopeSheetToProgram(sheet);

  const areas = {};
  Object.entries(AREA_BY_SECTION).forEach(([sectionId, roomType]) => {
    const sectionData = sheet[sectionId] || {};
    areas[roomType] = {
      room_type: roomType,
      title:
        existingScope?.areas?.[roomType]?.title ||
        roomType.replace(/_/g, ' '),
      answers: flattenAnswers(sectionData),
      notes: areaNotesFromSection(sectionData),
    };
  });

  const existingRooms = Array.isArray(existingScope?.rooms)
    ? existingScope.rooms
    : Array.isArray(existingScope?.raw?.rooms)
      ? existingScope.raw.rooms
      : [];

  const rooms = [];

  const bedroomCount = program.bedroom_count;
  for (let i = 1; i <= bedroomCount; i += 1) {
    const sectionData = sheet[`bedroom-${i}`] || {};
    const existing = existingRooms.find(
      (r) => r.room_type === 'bedroom_suite' && Number(r.index) === i
    );
    rooms.push({
      instance_id: existing?.instance_id || existing?.instanceId,
      room_type: 'bedroom_suite',
      index: i,
      label: existing?.label || `Bedroom ${i}`,
      answers: flattenAnswers(sectionData),
      notes: areaNotesFromSection(sectionData),
    });
  }

  const balconies = sheet?.balconies?.balconies || [];
  balconies.forEach((balcony, idx) => {
    const index = idx + 1;
    const existing = existingRooms.find(
      (r) => r.room_type === 'balcony' && Number(r.index) === index
    );
    const answers = {};
    Object.entries(balcony || {}).forEach(([groupId, values]) => {
      if (groupId === 'label') {
        answers.label = balcony.label || '';
        return;
      }
      if (!isPlainObject(values)) return;
      Object.entries(values).forEach(([fieldId, value]) => {
        answers[`${groupId}__${fieldId}`] = value;
      });
    });
    rooms.push({
      instance_id: existing?.instance_id || existing?.instanceId,
      room_type: 'balcony',
      index,
      label: balcony.label || `Balcony ${index}`,
      answers,
      notes: emptyTipTapNotes(),
    });
  });

  if (program.help_room_count > 0 && sheet['help-room']) {
    const existing = existingRooms.find((r) => r.room_type === 'help_room');
    rooms.push({
      instance_id: existing?.instance_id || existing?.instanceId,
      room_type: 'help_room',
      index: 1,
      label: existing?.label || 'Help Room',
      answers: flattenAnswers(sheet['help-room']),
      notes: areaNotesFromSection(sheet['help-room']),
    });
  }

  if (program.store_room_count > 0 && sheet['store-room']) {
    const existing = existingRooms.find((r) => r.room_type === 'store_room');
    rooms.push({
      instance_id: existing?.instance_id || existing?.instanceId,
      room_type: 'store_room',
      index: 1,
      label: existing?.label || 'Store Room',
      answers: flattenAnswers(sheet['store-room']),
      notes: areaNotesFromSection(sheet['store-room']),
    });
  }

  const docNotesRaw = sheet?.['section-notes']?.notes?.notes;
  let document_notes = emptyTipTapNotes();
  if (docNotesRaw && typeof docNotesRaw === 'object' && docNotesRaw.format === 'tiptap') {
    document_notes = normalizeTipTapNotes(docNotesRaw);
  } else if (typeof docNotesRaw === 'string' && docNotesRaw.trim()) {
    document_notes = { format: 'tiptap', doc: htmlToTipTap(docNotesRaw) };
  } else if (existingScope?.documentNotes) {
    document_notes = normalizeTipTapNotes(existingScope.documentNotes);
  }

  // PATCH rooms must only include instances that already have instance_id (from expand)
  const roomsForPatch = rooms
    .filter((r) => r.instance_id)
    .map(({ instance_id, label, answers, notes }) => ({
      instance_id,
      label,
      answers,
      notes,
    }));

  return {
    program,
    areas,
    rooms: roomsForPatch,
    document_notes,
    _roomsAll: rooms,
  };
};

/** Convert API scope document → FE scopeSheet for the existing form. */
export const apiScopeToScopeSheet = (scope) => {
  const base = emptyScopeSheet();
  if (!scope) return base;

  const program = scope.program || scope.raw?.program || {};
  const areas = scope.areas || scope.raw?.areas || {};
  const rooms = scope.rooms || scope.raw?.rooms || [];

  // Program → Section A / B / C
  base.A = {
    ...base.A,
    basicInfo: {
      ...(base.A.basicInfo || {}),
      projectAddress: program.project_address || '',
      clientNames: program.client_names || '',
      propertyType: program.property_type
        ? [program.property_type]
        : base.A.basicInfo?.propertyType || [],
      unitType:
        program.unit_type ||
        (program.bedroom_count ? `${program.bedroom_count}BHK` : ''),
      totalCarpetArea: program.total_carpet_area || '',
    },
  };
  base.B = {
    servicesRequired: {
      selected: Array.isArray(program.services_required)
        ? program.services_required
        : [],
      notes: '',
    },
  };
  base.C = {
    deliverables: {
      selected: Array.isArray(program.deliverables) ? program.deliverables : [],
      notes: '',
    },
  };

  Object.entries(areas).forEach(([roomType, area]) => {
    const sectionId = SECTION_BY_AREA[roomType];
    if (!sectionId) return;
    const unflat = unflattenAnswers(area?.answers || {});
    base[sectionId] = {
      ...(base[sectionId] || {}),
      ...unflat,
    };
    if (area?.notes) {
      base[sectionId].notes = {
        notes: normalizeTipTapNotes(area.notes),
      };
    }
  });

  const bedrooms = rooms
    .filter((r) => r.room_type === 'bedroom_suite')
    .sort((a, b) => Number(a.index) - Number(b.index));
  bedrooms.forEach((room) => {
    const id = `bedroom-${room.index}`;
    base[id] = {
      ...(base[id] || {}),
      ...unflattenAnswers(room.answers || {}),
    };
    if (room.notes) {
      base[id].notes = { notes: normalizeTipTapNotes(room.notes) };
    }
  });

  const balconies = rooms
    .filter((r) => r.room_type === 'balcony')
    .sort((a, b) => Number(a.index) - Number(b.index));
  if (balconies.length) {
    base.balconies = {
      balconies: balconies.map((room) => {
        const unflat = unflattenAnswers(room.answers || {});
        return {
          label: room.label || room.answers?.label || '',
          ...unflat,
        };
      }),
    };
  }

  const help = rooms.find((r) => r.room_type === 'help_room');
  if (help) {
    base['help-room'] = {
      ...(base['help-room'] || {}),
      ...unflattenAnswers(help.answers || {}),
    };
  }

  const store = rooms.find((r) => r.room_type === 'store_room');
  if (store) {
    base['store-room'] = {
      ...(base['store-room'] || {}),
      ...unflattenAnswers(store.answers || {}),
    };
  }

  const docNotes = normalizeTipTapNotes(
    scope.documentNotes || scope.raw?.document_notes
  );
  base['section-notes'] = {
    notes: {
      notes: docNotes,
    },
  };

  return base;
};

export const tipTapDocToPreviewText = tipTapToPlain;

export { AREA_BY_SECTION, SECTION_BY_AREA };
